import React, { useCallback, useEffect, useState } from "react";
import MetaTags from "react-meta-tags";
import axios from "../api/axios";
import jwt_decode from "jwt-decode";
import {
  Table,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Modal,
  Label,
  Input,
  Form,
} from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";

// Partner API keys for the public-form read endpoints (property list, request
// types, entry permissions). Company account only. A key is shown exactly once,
// right after it is generated; the list afterwards shows only its prefix.

const LIST_URL = "/company/api-keys";
const API_BASE = (process.env.REACT_APP_SERVER_URL || "https://www.rentdigicare.ca/api").replace(/\/$/, "");

const fmtDate = (d) => (d ? new Date(d).toLocaleString() : "—");

// A failure with no JSON message is almost always a 404 from a server that
// does not have these routes yet (wrong API target, or not restarted) — say so
// instead of a bare "could not", so the cause is obvious.
const describeError = (err, what) => {
  const fromServer = err?.response?.data?.message;
  if (fromServer) return fromServer;
  const status = err?.response?.status;
  const target = err?.config?.baseURL || err?.config?.url || "";
  if (status === 404) {
    return `${what}: the API at ${target} does not have /company/api-keys (HTTP 404). Point the app at a server running this version and restart it.`;
  }
  if (status) return `${what} (HTTP ${status} from ${target})`;
  return `${what}: no response from ${target || "the API"} (network error)`;
};

const STATUS_STYLE = {
  active: { background: "#ECFDF5", color: "#047857", border: "1px solid #A7F3D0" },
  revoked: { background: "#F1F5F9", color: "#64748B", border: "1px solid #CBD5E1" },
};

const ApiAccess = () => {
  const decode = jwt_decode(window.localStorage.accessToken);
  const canManage = decode.role === "company";

  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [label, setLabel] = useState("");
  const [creating, setCreating] = useState(false);
  const [createdKey, setCreatedKey] = useState(""); // shown once
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(LIST_URL);
      if (res.data?.status === 200) setKeys(res.data.keys || []);
      else setError(res.data?.message || "Could not load API keys");
    } catch (e) {
      setError(describeError(e, "Could not load API keys"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openModal = () => {
    setLabel("");
    setCreatedKey("");
    setCopied(false);
    setModalOpen(true);
  };

  const generate = async (e) => {
    e.preventDefault();
    if (creating) return;
    setCreating(true);
    try {
      const res = await axios.post(LIST_URL, { label: label.trim() });
      if (res.data?.status === 200 && res.data.key) {
        setCreatedKey(res.data.key);
        load();
      } else {
        setError(res.data?.message || "Could not generate a key");
        setModalOpen(false);
      }
    } catch (err) {
      setError(describeError(err, "Could not generate a key"));
      setModalOpen(false);
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (k) => {
    if (!window.confirm(`Revoke key ${k.prefix}…? Any website using it will lose access immediately.`)) return;
    try {
      const res = await axios.delete(`${LIST_URL}/${k._id}`);
      if (res.data?.status === 200) load();
      else setError(res.data?.message || "Could not revoke the key");
    } catch (err) {
      setError(describeError(err, "Could not revoke the key"));
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(createdKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard unavailable — the key is selectable below */
    }
  };

  const activeCount = keys.filter((k) => !k.revokedAt).length;

  return (
    <React.Fragment>
      <div className="page-content">
        <MetaTags>
          <title>Rentdigicare | API Access</title>
        </MetaTags>
        <div className="container-fluid">
          <Breadcrumbs title="Home" breadcrumbItem="API Access" />
          <Row>
            <Col xl={12}>
              <Card>
                <CardHeader>
                  <div className="row align-items-center">
                    <div className="col-md-8">
                      <h5 className="card-title mb-1">
                        Partner API keys
                        <span className="text-muted fw-normal ms-2">({activeCount} active)</span>
                      </h5>
                      <p className="text-muted mb-0" style={{ fontSize: "0.82rem" }}>
                        Keys let the websites you approve read this company's <strong>properties</strong>,{" "}
                        <strong>request types</strong> and <strong>entry permissions</strong> to build a
                        maintenance form. Until you generate your first key these endpoints stay open as before;{" "}
                        <strong>from then on callers must present an active key</strong> — revoking keys never
                        reopens them. Your own public maintenance page keeps working without a key.
                      </p>
                    </div>
                    <div className="col-md-4 d-flex justify-content-end">
                      {canManage && (
                        <button type="button" onClick={openModal} className="btn btn-light">
                          <i className="bx bx-plus me-1"></i> Generate key
                        </button>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardBody>
                  {error && (
                    <div className="alert alert-danger py-2" role="alert">
                      {error}
                    </div>
                  )}
                  <div className="table-responsive">
                    <Table className="table-striped table-bordered mb-0">
                      <thead>
                        <tr>
                          <th scope="col">#</th>
                          <th scope="col">Label</th>
                          <th scope="col">Key</th>
                          <th scope="col">Created</th>
                          <th scope="col">Last used</th>
                          <th scope="col">Status</th>
                          {canManage && <th scope="col">Action</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {keys.length === 0 && (
                          <tr>
                            <td colSpan={canManage ? 7 : 6} className="text-muted text-center py-4">
                              {loading ? "Loading…" : "No API keys yet. These endpoints are open to anyone who knows your company id until you generate one."}
                            </td>
                          </tr>
                        )}
                        {keys.map((k, i) => {
                          const revoked = !!k.revokedAt;
                          return (
                            <tr key={k._id}>
                              <td>{i + 1}</td>
                              <td>{k.label || <span className="text-muted">—</span>}</td>
                              <td>
                                <code>{k.prefix}…</code>
                              </td>
                              <td>{fmtDate(k.createdAt)}</td>
                              <td>{fmtDate(k.lastUsedAt)}</td>
                              <td>
                                <span
                                  className="badge rounded-pill"
                                  style={{ ...STATUS_STYLE[revoked ? "revoked" : "active"], fontWeight: 600, padding: "5px 10px" }}
                                >
                                  {revoked ? `Revoked ${fmtDate(k.revokedAt)}` : "Active"}
                                </span>
                              </td>
                              {canManage && (
                                <td>
                                  {!revoked && (
                                    <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => revoke(k)}>
                                      Revoke
                                    </button>
                                  )}
                                </td>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </div>

                  <div className="mt-4">
                    <h6 className="mb-2">How a partner website uses a key</h6>
                    <pre className="bg-light p-3 rounded" style={{ fontSize: "0.78rem", marginBottom: 0 }}>
{`# Send the key from your SERVER (never from browser code):
GET  ${API_BASE}/property/getpropertyList/list?company_id=${decode.id}
POST ${API_BASE}/requesttype/getrequest        { "company_id": "${decode.id}" }
POST ${API_BASE}/permissiontype/getpermissions { "company_id": "${decode.id}" }
     x-api-key: rdc_…`}
                    </pre>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Modal size="lg" isOpen={modalOpen} toggle={() => setModalOpen(!modalOpen)}>
            <div className="modal-header">
              <h5 className="modal-title mt-0">{createdKey ? "Your new API key" : "Generate API key"}</h5>
              <button type="button" className="close" aria-label="Close" onClick={() => setModalOpen(false)}>
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
            <div className="modal-body">
              {createdKey ? (
                <>
                  <div className="alert alert-warning py-2" role="alert">
                    Copy this key now — it is shown <strong>once</strong>. If you lose it, revoke it and generate a new one.
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <Input type="text" readOnly value={createdKey} style={{ fontFamily: "monospace" }} onFocus={(e) => e.target.select()} />
                    <button type="button" className="btn btn-primary" onClick={copy} style={{ whiteSpace: "nowrap" }}>
                      {copied ? "Copied" : "Copy"}
                    </button>
                  </div>
                  <div className="text-end mt-3">
                    <button type="button" className="btn btn-success" onClick={() => setModalOpen(false)}>
                      Done
                    </button>
                  </div>
                </>
              ) : (
                <Form onSubmit={generate}>
                  <div className="mb-3">
                    <Label className="form-label" htmlFor="api-key-label">
                      Label <span className="text-muted fw-normal">(who uses it — e.g. "gskproperties.ca website")</span>
                    </Label>
                    <Input
                      id="api-key-label"
                      type="text"
                      maxLength={60}
                      value={label}
                      onChange={(e) => setLabel(e.target.value)}
                      placeholder="Partner website name"
                    />
                  </div>
                  <div className="text-end">
                    <button type="submit" className="btn btn-success" disabled={creating}>
                      {creating ? "Generating…" : "Generate"}
                    </button>
                  </div>
                </Form>
              )}
            </div>
          </Modal>
        </div>
      </div>
    </React.Fragment>
  );
};

export default ApiAccess;
